import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaFileExcel, FaSpinner, FaMapMarkedAlt } from 'react-icons/fa';
import { FiCheck, FiExternalLink, FiEye, FiMapPin, FiNavigation, FiSearch, FiX } from 'react-icons/fi';
import toast from 'react-hot-toast';
import axiosInstance from '../../libs/axios';
import { Pagination, PaginationType } from '../../utils/pagination.utils';
import { SelectWithSearch, type SelectOption } from '../../utils/select-with-search';
import { useAuth } from '../../provider/auth.provider';
import { dateRangeError, dateRangeInputBounds } from '../../utils/date-range.utils';

/**
 * Wrong Site Location
 * ===================
 *
 * Engineers report from the mobile app that a survey site's registered
 * latitude/longitude is wrong, from wherever they are standing. Each row holds
 * the site's coordinates at the time, the engineer's own position, and the
 * aerial distance between the two.
 *
 * Scoped by the API to the viewer's projects (super admin: all), rows and the
 * Project / Reported By dropdowns alike. Resolving / rejecting needs
 * reports.manage_site_location_report; resolving can also copy the engineer's
 * position onto the site.
 */

type ReportStatus = 'open' | 'resolved' | 'rejected';

interface ReportRow {
  id: number;
  status: ReportStatus;
  reported_at: string;
  remarks: string | null;
  site_pk: number;
  site_id: string | null;
  site_name: string | null;
  site_address: string | null;
  pincode: string | number | null;
  district: string | null;
  state: string | null;
  site_status: string | null;
  site_workflow_status: string | null;
  site_status_at_report: string | null;
  site_workflow_status_at_report: string | null;
  project_id: number;
  project_name: string | null;
  project_short_name: string | null;
  activity_name: string | null;
  reporter_id: number;
  reporter_name: string | null;
  reporter_employee_code: string | null;
  reporter_phone: string | null;
  site_engineer_at_report: string | null;
  current_site_engineer: string | null;
  site_latitude: number | null;
  site_longitude: number | null;
  reporter_latitude: number | null;
  reporter_longitude: number | null;
  reporter_accuracy_m: number | null;
  distance_km: number | null;
  current_site_latitude: number | null;
  current_site_longitude: number | null;
  fw_start_latitude: number | null;
  fw_start_longitude: number | null;
  fw_start_at: string | null;
  site_map_url: string | null;
  reporter_map_url: string | null;
  directions_url: string | null;
  resolved_by_name: string | null;
  resolved_at: string | null;
  resolution_remarks: string | null;
  location_applied: boolean;
}

interface Filters {
  search: string;
  project_id: string;
  reported_by: string;
  date_from: string;
  date_to: string;
  min_distance_km: string;
  sort_by: 'created_at' | 'distance_km';
}

const DEFAULT_FILTERS: Filters = {
  search: '',
  project_id: '',
  reported_by: '',
  date_from: '',
  date_to: '',
  min_distance_km: '',
  sort_by: 'created_at',
};

const STATUS_TABS: Array<{ key: ReportStatus | 'all'; label: string }> = [
  { key: 'open', label: 'Open' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

const STATUS_BADGE: Record<string, string> = {
  open: 'bg-amber-100 text-amber-800',
  resolved: 'bg-green-100 text-green-800',
  rejected: 'bg-gray-200 text-gray-700',
};

const EMPTY_PAGINATION: PaginationType = {
  offset: 0,
  limit: 25,
  page: 1,
  total_pages: 1,
  total_count: 0,
  has_next_page: false,
  has_prev_page: false,
  next_page: null,
  prev_page: null,
};

const formatDateTime = (value: unknown): string => {
  if (!value) return 'NA';
  const date = new Date(value as string);
  if (Number.isNaN(date.getTime())) return 'NA';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const coords = (lat: number | null, lng: number | null) =>
  lat === null || lng === null ? 'NA' : `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

const distanceLabel = (km: number | null) => {
  if (km === null || km === undefined) return 'NA';
  return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(2)} km`;
};

const humanize = (value: string | null) => (value ? value.replace(/_/g, ' ') : 'NA');

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between gap-4 py-1.5 text-sm">
    <span className="text-gray-500 shrink-0">{label}</span>
    <span className="text-gray-900 text-right break-words">{children}</span>
  </div>
);

const MapLink: React.FC<{ href: string | null; label: string; icon?: React.ReactNode }> = ({ href, label, icon }) =>
  href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 text-sm text-blue-700 hover:text-blue-900 hover:underline"
    >
      {icon ?? <FiExternalLink className="w-3.5 h-3.5" />}
      {label}
    </a>
  ) : null;

/** Right-side details panel for one report. */
const DetailsDrawer: React.FC<{
  row: ReportRow | null;
  onClose: () => void;
  canManage: boolean;
  onDecide: (row: ReportRow, status: 'resolved' | 'rejected') => void;
}> = ({ row, onClose, canManage, onDecide }) => {
  if (!row) return null;
  const moved =
    row.current_site_latitude !== row.site_latitude || row.current_site_longitude !== row.site_longitude;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-xl h-full bg-white shadow-xl flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {row.site_id || 'Site'} <span className="text-gray-500 font-normal">— location report #{row.id}</span>
            </h3>
            <p className="text-sm text-gray-500">
              Reported {formatDateTime(row.reported_at)} by {row.reporter_name || 'NA'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600" title="Close">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_BADGE[row.status]}`}>
              {row.status}
            </span>
            {row.location_applied && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Site location updated
              </span>
            )}
          </div>

          {/* The two points side by side */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="border border-gray-200 rounded p-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-1">
                <FiMapPin className="w-4 h-4 text-red-600" /> Site location (at report)
              </div>
              <div className="font-mono text-sm text-gray-900">{coords(row.site_latitude, row.site_longitude)}</div>
              <div className="mt-2">
                <MapLink href={row.site_map_url} label="Open in Maps" />
              </div>
            </div>
            <div className="border border-gray-200 rounded p-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-1">
                <FiNavigation className="w-4 h-4 text-blue-700" /> Engineer's position
              </div>
              <div className="font-mono text-sm text-gray-900">
                {coords(row.reporter_latitude, row.reporter_longitude)}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                GPS accuracy: {row.reporter_accuracy_m !== null ? `±${Math.round(row.reporter_accuracy_m)} m` : 'not sent'}
              </div>
              <div className="mt-2">
                <MapLink href={row.reporter_map_url} label="Open in Maps" />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between rounded bg-gray-50 border border-gray-200 px-3 py-2">
            <span className="text-sm text-gray-600">Aerial distance between the two</span>
            <span className={`text-sm font-semibold ${row.distance_km !== null && row.distance_km > 0.5 ? 'text-red-700' : 'text-gray-900'}`}>
              {distanceLabel(row.distance_km)}
            </span>
          </div>
          <MapLink href={row.directions_url} label="Directions from engineer to registered site" icon={<FaMapMarkedAlt className="w-3.5 h-3.5" />} />

          {row.remarks && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Engineer's remarks</h4>
              <p className="text-sm text-gray-800 whitespace-pre-wrap bg-amber-50 border border-amber-100 rounded p-3">{row.remarks}</p>
            </div>
          )}

          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Site</h4>
            <div className="divide-y divide-gray-100">
              <DetailRow label="Site ID">{row.site_id || 'NA'}</DetailRow>
              <DetailRow label="Site name">{row.site_name || 'NA'}</DetailRow>
              <DetailRow label="Address">{row.site_address || 'NA'}</DetailRow>
              <DetailRow label="District / State">
                {[row.district, row.state].filter(Boolean).join(', ') || 'NA'}
              </DetailRow>
              <DetailRow label="Pincode">{row.pincode ?? 'NA'}</DetailRow>
              <DetailRow label="Project">{row.project_name || 'NA'}</DetailRow>
              <DetailRow label="Activity">{row.activity_name || 'NA'}</DetailRow>
              <DetailRow label="Stage at report">
                <span className="capitalize">{humanize(row.site_workflow_status_at_report)}</span>
              </DetailRow>
              <DetailRow label="Stage now">
                <span className="capitalize">{humanize(row.site_workflow_status)}</span>
              </DetailRow>
              <DetailRow label="Site engineer at report">{row.site_engineer_at_report || 'Not assigned'}</DetailRow>
              <DetailRow label="Current site location">
                <span className="font-mono">{coords(row.current_site_latitude, row.current_site_longitude)}</span>
                {moved && <span className="block text-xs text-blue-700">changed since the report</span>}
              </DetailRow>
              <DetailRow label="Field work started from">
                {row.fw_start_latitude !== null ? (
                  <>
                    <span className="font-mono">{coords(row.fw_start_latitude, row.fw_start_longitude)}</span>
                    <span className="block text-xs text-gray-500">{formatDateTime(row.fw_start_at)}</span>
                  </>
                ) : (
                  'Field work not started'
                )}
              </DetailRow>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Reported by</h4>
            <div className="divide-y divide-gray-100">
              <DetailRow label="Name">{row.reporter_name || 'NA'}</DetailRow>
              <DetailRow label="Employee code">{row.reporter_employee_code || 'NA'}</DetailRow>
              <DetailRow label="Phone">
                {row.reporter_phone ? (
                  <a href={`tel:${row.reporter_phone}`} className="text-blue-700 hover:underline">{row.reporter_phone}</a>
                ) : (
                  'NA'
                )}
              </DetailRow>
            </div>
          </div>

          {row.status !== 'open' && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Decision</h4>
              <div className="divide-y divide-gray-100">
                <DetailRow label="Decided by">{row.resolved_by_name || 'NA'}</DetailRow>
                <DetailRow label="Decided on">{formatDateTime(row.resolved_at)}</DetailRow>
                <DetailRow label="Remarks">{row.resolution_remarks || '—'}</DetailRow>
                <DetailRow label="Site location updated">{row.location_applied ? 'Yes' : 'No'}</DetailRow>
              </div>
            </div>
          )}
        </div>

        {canManage && row.status === 'open' && (
          <div className="px-5 py-4 border-t border-gray-200 flex gap-3">
            <button
              onClick={() => onDecide(row, 'rejected')}
              className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50 text-sm font-medium"
            >
              Reject
            </button>
            <button
              onClick={() => onDecide(row, 'resolved')}
              className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm font-medium"
            >
              Resolve
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/** Resolve / reject prompt, with the option to correct the site's location. */
const DecisionModal: React.FC<{
  row: ReportRow | null;
  status: 'resolved' | 'rejected';
  onClose: () => void;
  onDone: () => void;
}> = ({ row, status, onClose, onDone }) => {
  const [remarks, setRemarks] = useState('');
  const [applyLocation, setApplyLocation] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRemarks('');
    setApplyLocation(false);
  }, [row, status]);

  if (!row) return null;
  const isResolve = status === 'resolved';
  const canSubmit = isResolve || remarks.trim().length > 0;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      await axiosInstance.patch(`/reports/site-location/${row.id}/status`, {
        status,
        ...(remarks.trim() ? { resolution_remarks: remarks.trim() } : {}),
        ...(isResolve ? { apply_location: applyLocation } : {}),
      });
      toast.success(isResolve ? 'Report resolved' : 'Report rejected');
      onDone();
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to update the report');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={submit} className="w-full max-w-md bg-white rounded shadow-xl">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {isResolve ? 'Resolve report' : 'Reject report'} — {row.site_id}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FiX className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {isResolve ? (
            <label className="flex items-start gap-3 rounded border border-blue-200 bg-blue-50 p-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={applyLocation}
                onChange={(e) => setApplyLocation(e.target.checked)}
              />
              <span className="text-sm text-blue-900">
                Update the site's coordinates to the engineer's position
                <span className="block font-mono text-xs mt-1">
                  {coords(row.site_latitude, row.site_longitude)} → {coords(row.reporter_latitude, row.reporter_longitude)}
                </span>
              </span>
            </label>
          ) : (
            <p className="text-sm text-gray-600">
              Rejecting means the registered location was checked and is correct. The engineer can report again.
            </p>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Remarks {isResolve ? '(optional)' : '*'}
            </label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={3}
              maxLength={2000}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={isResolve ? 'What was done' : 'Why the registered location is right'}
            />
          </div>
        </div>
        <div className="px-5 pb-5 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50 text-sm font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !canSubmit}
            className={`flex-1 px-4 py-2 rounded text-white text-sm font-medium disabled:opacity-60 ${isResolve ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
          >
            {saving ? 'Saving...' : isResolve ? 'Resolve' : 'Reject'}
          </button>
        </div>
      </form>
    </div>
  );
};

const SiteLocationReport: React.FC = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('reports', 'manage_site_location_report');

  const [projectOptions, setProjectOptions] = useState<SelectOption[]>([]);
  const [reporterOptions, setReporterOptions] = useState<SelectOption[]>([]);
  const [selectedProject, setSelectedProject] = useState<SelectOption | null>(null);
  const [selectedReporter, setSelectedReporter] = useState<SelectOption | null>(null);

  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState<ReportStatus | 'all'>('open');
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({ open: 0, resolved: 0, rejected: 0 });
  const [pagination, setPagination] = useState<PaginationType>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [detailRow, setDetailRow] = useState<ReportRow | null>(null);
  const [decision, setDecision] = useState<{ row: ReportRow; status: 'resolved' | 'rejected' } | null>(null);

  useEffect(() => {
    const handle = setTimeout(() => setFilters((prev) => ({ ...prev, search: searchInput.trim() })), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    axiosInstance
      .get('/reports/site-location/projects')
      .then((response) => {
        setProjectOptions(
          (response.data?.data?.projects || []).map((project: any) => ({
            id: project.id,
            value: project.id,
            label: project.project_short_name
              ? `${project.project_name} (${project.project_short_name})`
              : project.project_name,
          })),
        );
      })
      .catch((error) => toast.error(error?.response?.data?.message || 'Failed to load projects'));
  }, []);

  // The reporter list follows the picked project, so it never offers someone
  // who cannot appear in the result.
  useEffect(() => {
    axiosInstance
      .get('/reports/site-location/reporters', {
        params: filters.project_id ? { project_id: filters.project_id } : undefined,
      })
      .then((response) => {
        setReporterOptions(
          (response.data?.data?.users || []).map((user: any) => ({
            id: user.id,
            value: user.id,
            label: user.employee_code ? `${user.name} (${user.employee_code})` : user.name,
          })),
        );
      })
      .catch(() => setReporterOptions([]));
  }, [filters.project_id]);

  const buildParams = useCallback(
    (page: number, limit: number) => {
      const params: Record<string, string | number> = { page, limit, status };
      (Object.keys(filters) as Array<keyof Filters>).forEach((key) => {
        if (filters[key]) params[key] = filters[key];
      });
      params.sort_order = 'DESC';
      return params;
    },
    [filters, status],
  );

  // The table refetches on every filter change and the export reads the same
  // filters, so an over-long From/To is stopped before either request; the
  // table keeps its last rows until the range is fixed.
  const dateError = dateRangeError(filters.date_from, filters.date_to, 'Date range');
  const dateBounds = dateRangeInputBounds(filters.date_from, filters.date_to);

  const fetchRows = useCallback(
    async (page = 1, limit = pagination.limit) => {
      if (dateError) {
        toast.error(dateError, { id: 'site-location-date-range' });
        return;
      }
      setLoading(true);
      try {
        const response = await axiosInstance.get('/reports/site-location', { params: buildParams(page, limit) });
        const payload = response.data?.data || {};
        setRows(payload.data || []);
        setCounts(payload.counts || { open: 0, resolved: 0, rejected: 0 });
        if (payload.pagination) setPagination(payload.pagination);
      } catch (error: any) {
        toast.error(error?.response?.data?.message || 'Failed to load the report');
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildParams],
  );

  useEffect(() => {
    fetchRows(1, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, status]);

  const handleExport = async () => {
    if (dateError) {
      toast.error(dateError, { id: 'site-location-date-range' });
      return;
    }
    try {
      setExporting(true);
      const response = await axiosInstance.get('/reports/site-location/export', {
        params: buildParams(1, pagination.limit),
        responseType: 'blob',
      });
      const disposition = String(response.headers?.['content-disposition'] || '');
      const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const fileName = match ? decodeURIComponent(match[1]) : 'wrong-site-location.xlsx';
      const blobUrl = window.URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Export downloaded');
    } catch (error: any) {
      let message = 'Failed to export the report';
      const data = error?.response?.data;
      if (data instanceof Blob) {
        try {
          message = JSON.parse(await data.text())?.message || message;
        } catch {
          /* not JSON */
        }
      } else if (data?.message) {
        message = data.message;
      }
      toast.error(message);
    } finally {
      setExporting(false);
    }
  };

  const setFilter = (key: keyof Filters, value: string) => setFilters((prev) => ({ ...prev, [key]: value }));

  const activeFilterCount = useMemo(
    () =>
      (['search', 'project_id', 'reported_by', 'date_from', 'date_to', 'min_distance_km'] as Array<keyof Filters>).filter(
        (key) => Boolean(filters[key]),
      ).length,
    [filters],
  );

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchInput('');
    setSelectedProject(null);
    setSelectedReporter(null);
  };

  const refreshAfterDecision = async () => {
    await fetchRows(pagination.page, pagination.limit);
    setDetailRow(null);
  };

  const columnCount = 10;

  return (
    <div className="space-y-4">
      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded bg-red-50 text-red-700 flex items-center justify-center">
                <FiMapPin className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Wrong Site Location</h1>
                <p className="text-sm text-gray-500">
                  Engineers' reports that a site's latitude/longitude is wrong, with where they were when reporting.
                </p>
              </div>
            </div>
            <button
              onClick={handleExport}
              disabled={exporting}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed shadow-sm"
            >
              {exporting ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaFileExcel className="w-4 h-4" />}
              {exporting ? 'Preparing...' : 'Export Excel'}
            </button>
          </div>
        </div>

        {/* Status tabs */}
        <div className="px-4 sm:px-6 pt-3 border-b border-gray-200 flex flex-wrap gap-1">
          {STATUS_TABS.map((tab) => {
            const count =
              tab.key === 'all'
                ? (counts.open || 0) + (counts.resolved || 0) + (counts.rejected || 0)
                : counts[tab.key] || 0;
            const active = status === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setStatus(tab.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                  active ? 'border-blue-800 text-blue-800' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                <span
                  className={`min-w-5 px-1.5 py-0.5 rounded-full text-xs ${
                    active ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3 items-end">
            <div className="xl:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Site ID or name"
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Project</label>
              <SelectWithSearch
                placeholder="All my projects"
                value={selectedProject}
                options={projectOptions}
                onChange={(option) => {
                  setSelectedProject(option);
                  // The reporter list is scoped to the project, so a reporter
                  // picked under the old one may not be in it any more.
                  setSelectedReporter(null);
                  setFilters((prev) => ({
                    ...prev,
                    project_id: option ? String(option.value) : '',
                    reported_by: '',
                  }));
                }}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Reported By</label>
              <SelectWithSearch
                placeholder="Anyone"
                value={selectedReporter}
                options={reporterOptions}
                onChange={(option) => {
                  setSelectedReporter(option);
                  setFilter('reported_by', option ? String(option.value) : '');
                }}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">From</label>
              <input
                type="date"
                value={filters.date_from}
                {...dateBounds.from}
                onChange={(e) => setFilter('date_from', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">To</label>
              <input
                type="date"
                value={filters.date_to}
                {...dateBounds.to}
                onChange={(e) => setFilter('date_to', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Distance</label>
              <select
                value={filters.min_distance_km}
                onChange={(e) => setFilter('min_distance_km', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Any distance</option>
                <option value="0.1">100 m or more</option>
                <option value="0.5">500 m or more</option>
                <option value="1">1 km or more</option>
                <option value="2">2 km or more</option>
                <option value="5">5 km or more</option>
              </select>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm">
            <div className="flex items-center gap-2">
              {activeFilterCount > 0 && (
                <>
                  <span className="text-gray-600">
                    {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'} applied
                  </span>
                  <button onClick={clearFilters} className="text-blue-700 hover:text-blue-900 underline">
                    Clear all
                  </button>
                </>
              )}
            </div>
            <label className="flex items-center gap-2 text-gray-600">
              Sort by
              <select
                value={filters.sort_by}
                onChange={(e) => setFilter('sort_by', e.target.value)}
                className="px-2 py-1 border border-gray-300 rounded text-sm bg-white"
              >
                <option value="created_at">Newest first</option>
                <option value="distance_km">Largest distance first</option>
              </select>
            </label>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Actions', 'Reported At', 'Site', 'Project', 'Reported By', 'Site Lat/Long', 'Engineer Lat/Long', 'Distance', 'Remarks', 'Status'].map(
                  (label) => (
                    <th key={label} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      {label}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={columnCount} className="px-4 py-12 text-center text-sm text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="px-4 py-12 text-center text-sm text-gray-500">
                    {activeFilterCount > 0 ? 'No reports match these filters.' : `No ${status === 'all' ? '' : status + ' '}reports.`}
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setDetailRow(row)} className="p-2 text-blue-700 hover:bg-blue-50 rounded" title="View details">
                          <FiEye className="w-4 h-4" />
                        </button>
                        {canManage && row.status === 'open' && (
                          <>
                            <button
                              onClick={() => setDecision({ row, status: 'resolved' })}
                              className="p-2 text-green-700 hover:bg-green-50 rounded"
                              title="Resolve"
                            >
                              <FiCheck className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDecision({ row, status: 'rejected' })}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                              title="Reject"
                            >
                              <FiX className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">{formatDateTime(row.reported_at)}</td>
                    <td className="px-4 py-4 text-sm">
                      <div className="font-semibold text-gray-900">{row.site_id || 'NA'}</div>
                      <div className="text-xs text-gray-500 max-w-[220px] truncate" title={row.site_name || ''}>
                        {row.site_name || ''}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      <div>{row.project_name || 'NA'}</div>
                      <div className="text-xs text-gray-500">{row.activity_name || ''}</div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">
                      <div>{row.reporter_name || 'NA'}</div>
                      <div className="text-xs text-gray-500">{row.reporter_employee_code || ''}</div>
                    </td>
                    <td className="px-4 py-4 text-sm whitespace-nowrap">
                      {row.site_map_url ? (
                        <a href={row.site_map_url} target="_blank" rel="noopener noreferrer" className="font-mono text-blue-700 hover:underline">
                          {coords(row.site_latitude, row.site_longitude)}
                        </a>
                      ) : (
                        <span className="text-gray-400">Not registered</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm whitespace-nowrap">
                      <a
                        href={row.reporter_map_url || undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-blue-700 hover:underline"
                      >
                        {coords(row.reporter_latitude, row.reporter_longitude)}
                      </a>
                      {row.reporter_accuracy_m !== null && (
                        <div className="text-xs text-gray-500">±{Math.round(row.reporter_accuracy_m)} m</div>
                      )}
                    </td>
                    <td
                      className={`px-4 py-4 text-sm whitespace-nowrap ${
                        row.distance_km !== null && row.distance_km > 0.5 ? 'font-semibold text-red-700' : 'text-gray-700'
                      }`}
                    >
                      {distanceLabel(row.distance_km)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700 max-w-[240px] truncate" title={row.remarks || ''}>
                      {row.remarks || '—'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_BADGE[row.status]}`}>
                        {row.status}
                      </span>
                      {row.location_applied && <div className="text-xs text-blue-700 mt-1">site updated</div>}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.total_count > 0 && (
          <div className="border-t border-gray-200">
            <Pagination
              pagination={pagination}
              onPageChange={(page) => fetchRows(page, pagination.limit)}
              onLimitChange={(limit) => fetchRows(1, limit)}
            />
          </div>
        )}
      </div>

      <DetailsDrawer
        row={detailRow}
        onClose={() => setDetailRow(null)}
        canManage={canManage}
        onDecide={(row, next) => setDecision({ row, status: next })}
      />
      <DecisionModal
        row={decision?.row ?? null}
        status={decision?.status ?? 'resolved'}
        onClose={() => setDecision(null)}
        onDone={refreshAfterDecision}
      />
    </div>
  );
};

export default SiteLocationReport;
